// ABOUTME: Generic Kratos self-service flow lifecycle (create/get/submit) for the in-app auth pages.
// ABOUTME: Handles session_already_available, redirect_browser_to, validation (400), expiry (410).

import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import type { UiNode, UiText } from "@ory/client"
import { useTranslation } from "react-i18next"
import { useNotificationContext } from "@/app/providers/useNotificationContext"
import { kratosErrorId, safeRedirect, safeReturnTo } from "./redirectGuard"
import { TAB_STORAGE_KEYS } from "@shared/constants/storageKeys"
import { useLatest } from "@shared/hooks"

interface FlowLike {
  id: string
  ui: { nodes: UiNode[]; messages?: UiText[] }
}

interface UseKratosFlowOptions<TFlow extends FlowLike> {
  createFlow: () => Promise<{ data: TFlow }>
  getFlow: (id: string) => Promise<{ data: TFlow }>
  updateFlow: (flowId: string, body: Record<string, unknown>) => Promise<{ data: unknown }>
  /** Adjust each adopted flow (e.g. registration forces the code method) — no DOM hacks. */
  transformFlow?: (flow: TFlow) => TFlow
  /** Custom success handling; return true if handled (skips the default redirect into the app). */
  onSuccess?: (data: unknown) => boolean | void
  /** Multi-step flows (verification, recovery step 1): re-render the returned flow instead of leaving. */
  stayOnSuccess?: boolean
  /** Intercept a `redirect_browser_to` (e.g. recovery → settings hand-off); return true to not follow it. */
  onRedirect?: (url: string) => boolean
  /** Load this flow id instead of reading `?flow` from the URL (e.g. the settings flow from recovery). */
  initialFlowId?: string
  /** Suppress the global error toast — for embedders that render the failure
   *  themselves (AccountSecurity shows an inline fallback), or where failure
   *  is an expected state (wrapper without a Kratos session cookie). */
  notifyOnError?: boolean
}

export function useKratosFlow<TFlow extends FlowLike>(opts: UseKratosFlowOptions<TFlow>) {
  const [flow, setFlow] = useState<TFlow | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [failed, setFailed] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { handleNotification } = useNotificationContext()
  const { t } = useTranslation()

  /*
   * The caller rebuilds this options object on every render, so it can never be a dependency key —
   * which is what four suppressions used to say. Two things replace them, and they are two because
   * the intent is not one intent.
   *
   * `mountOpts` is the snapshot from mount. adoptFlow and the initial load want the options as they
   * were then: RegistrationPage depends on that — it pins traits.language when the flow is first
   * adopted and re-stamps it at submit time precisely because a later language switch must not
   * change the adopted flow (RegistrationPage.tsx:70-76).
   *
   * The functions below are plain, not useCallback. Two reasons. They simply close over the options
   * the caller has now, which is what their old dependency lists approximated by being re-created on
   * every flow change; and a manual memo that reads a ref's `current` is one the React Compiler
   * cannot preserve, so hand-memoising them here is what would keep the compiler out. Their identity
   * changing per render costs nothing — the effect below reaches them through refs, and the compiler
   * memoises the hook in the build (vite.config.ts:146).
   */
  const mountOpts = useRef(opts)

  const adoptFlow = (f: TFlow) => {
    // A flow in hand means the screen is no longer in its failed state. Without
    // this, `restart` after a network blip fetched a perfectly good flow and the
    // error screen stayed up, with its own retry button the only thing on it.
    setFailed(false)
    setFlow(mountOpts.current.transformFlow ? mountOpts.current.transformFlow(f) : f)
  }

  const goToApp = useCallback(() => {
    const dest = safeReturnTo(searchParams.get("return_to"))
    // Forward a language explicitly chosen on the auth screen so it survives into the app
    // (LangLoad reads ?lang, applies it, and persists it to the account).
    let lang: string | null = null
    try {
      lang = sessionStorage.getItem(TAB_STORAGE_KEYS.AUTH_SELECTED_LANG)
      if (lang) sessionStorage.removeItem(TAB_STORAGE_KEYS.AUTH_SELECTED_LANG)
    } catch {
      /* ignore */
    }
    const to = lang ? `${dest}${dest.includes("?") ? "&" : "?"}lang=${encodeURIComponent(lang)}` : dest
    // `fromAuth` keeps the UserProvider loader on the auth screens' dark
    // background during the login → app hand-off (no white flash).
    navigate(to, { replace: true, state: { fromAuth: true } })
  }, [navigate, searchParams])

  const startFresh = () => {
    opts
      .createFlow()
      .then(({ data }) => {
        adoptFlow(data)
        setSearchParams(
          (sp) => {
            sp.set("flow", data.id)
            return sp
          },
          { replace: true },
        )
      })
      .catch((err: unknown) => {
        if (kratosErrorId(err) === "session_already_available") return goToApp()
        setFailed(true)
        if (opts.notifyOnError !== false) {
          handleNotification("error", t("auth.genericError"), true, 3000)
        }
      })
  }

  // Read once, like the options: startFresh and submit rewrite ?flow through setSearchParams, and an
  // effect that re-read it would refetch and overwrite the flow state they just set.
  const mountFlowIdInUrl = useRef(searchParams.get("flow"))
  const latestAdoptFlow = useLatest(adoptFlow)
  const latestGoToApp = useLatest(goToApp)
  const latestStartFresh = useLatest(startFresh)

  useEffect(() => {
    const flowId = mountOpts.current.initialFlowId ?? mountFlowIdInUrl.current
    ;(flowId ? mountOpts.current.getFlow(flowId) : mountOpts.current.createFlow())
      .then(({ data }) => latestAdoptFlow.current(data))
      .catch((err: unknown) => {
        if (kratosErrorId(err) === "session_already_available") return latestGoToApp.current()
        latestStartFresh.current()
      })
    // Every name here is a ref, so the flow is adopted exactly once — which is the contract — and
    // the list says so instead of a suppression hiding it.
  }, [latestAdoptFlow, latestGoToApp, latestStartFresh])

  const submit = (body: Record<string, unknown>) => {
    if (!flow) return
    setSubmitting(true)
    opts
      .updateFlow(flow.id, body)
      .then(({ data }) => {
        const success = (data ?? {}) as { redirect_browser_to?: string }
        if (success.redirect_browser_to) {
          if (opts.onRedirect?.(success.redirect_browser_to)) return
          window.location.href = safeRedirect(success.redirect_browser_to)
          return
        }
        if (opts.onSuccess?.(data)) return
        if (opts.stayOnSuccess) {
          adoptFlow(data as TFlow)
          return
        }
        goToApp()
      })
      .catch((err: unknown) => {
        if (kratosErrorId(err) === "session_already_available") return goToApp()
        const data = (err as { response?: { data?: unknown } }).response?.data
        const body2 = (data ?? {}) as { redirect_browser_to?: string; use_flow_id?: string }
        if (body2.redirect_browser_to) {
          if (opts.onRedirect?.(body2.redirect_browser_to)) return
          window.location.href = safeRedirect(body2.redirect_browser_to)
          return
        }
        // 400 → Kratos returns the flow with validation messages; re-render it.
        if (data && typeof data === "object" && "ui" in data) {
          adoptFlow(data as TFlow)
          return
        }
        // 410 → flow expired; refetch via use_flow_id, else start fresh.
        if (body2.use_flow_id) {
          opts
            .getFlow(body2.use_flow_id)
            .then(({ data: f }) => adoptFlow(f))
            .catch(startFresh)
          return
        }
        if (opts.notifyOnError !== false) {
          handleNotification("error", t("auth.genericError"), true, 3000)
        }
      })
      .finally(() => setSubmitting(false))
  }

  // restart = abandon the current flow (e.g. the code-entry step) and create a fresh one,
  // returning the user to method selection.
  return { flow, submit, submitting, failed, goToApp, restart: startFresh }
}
